/**
 * Offline-first sync. Everything is saved on the device first; this keeps the server the same,
 * out of sight.
 *
 * One round trip does it all: `POST /api/sync` carries the maps changed here and brings back
 * what changed anywhere else since this device's cursor (the server's own change counter —
 * never a clock). Plan images and thumbnails go up once each, separately.
 *
 * When it runs — and only then; there is no timer ticking while nothing is happening:
 *  - a change here: once the hand has rested (IDLE_MS), and at least every MAX_WAIT_MS while
 *    changes keep coming; at once when the app is put away;
 *  - coming to the app (launch, back to the foreground, back online): to hear of other devices'
 *    changes — once per arrival, however many events announce it (FOREGROUND_MIN_MS);
 *  - after a failure, or when work could not move, with work still waiting: again by itself
 *    with a growing pause (2 s … 5 min). With nothing waiting it never retries: the next visit
 *    to the app asks again.
 *
 * What it costs to run: a save looks only at the maps that changed (`dirtyIds`, `blobIds` —
 * hints kept in memory); the whole store is read through on arriving at the app and after any
 * failure, which also mends the hints whatever happened to them.
 *
 * What makes it safe:
 *  - every local write is one IndexedDB transaction (`mutateMap`): the editor saving while an
 *    answer is applied can never undo one another, and an upload only counts for the version
 *    that was sent;
 *  - the server answers each map on its own: one it refuses is set aside, never in the way;
 *  - one tab syncs at a time (a Web Lock), and tells the others what changed;
 *  - the cursor belongs to the signed-in user, a device holds one user's maps at a time
 *    (`adoptDevice`), and a sync under way stops the moment the session changes;
 *  - once a day the device checks the index of all the server holds against its own, and mends
 *    any difference — whatever the cause.
 */
import { MAX_SYNC_BYTES, type BlobStored, type MapRecord, type SyncResult } from '../../shared/api.ts';
import { getBlob, getKV, getMap, hasBlob, listMaps, mutateMap, setKV, type LocalMap } from '../store/idb.ts';
import { ApiFailure, OfflineError, apiFetch, apiJson, describeError } from './api.ts';
import { noteServerTime, now } from './clock.ts';
import { applyAck, applyRecord, checkIndex, mapSignature, stableList, toPut } from './reconcile.ts';
import { app } from './state.ts';

const IDLE_MS = 2_000;
const MAX_WAIT_MS = 30_000;
/** Arriving at the app is several events at once (launch, pageshow, visible): they make one sync. */
const FOREGROUND_MIN_MS = 3_000;
const CHECK_EVERY_MS = 24 * 3600 * 1000;
/** The documents sent in one request (the rest follow in the next): well inside the server's limit. */
const BATCH_BYTES = Math.min(1_500_000, MAX_SYNC_BYTES / 2);

export type SyncReason = 'change' | 'foreground' | 'now';

let timer = 0;
let timerAt = Infinity;
let firstChangeAt = 0;
let running: Promise<void> | null = null;
let again = false;
/** Cycles in a row that failed, or ended with work that could not move: the pause grows with it. */
let setbacks = 0;
let lastOkAt = 0;
/** Maps known to have something to send (a hint: the store is the truth, read through when `thorough`). */
const dirtyIds = new Set<string>();
const blobIds = new Set<string>();
let thorough = true;
/** Moves when the local data is cleared: a cycle begun before writes nothing after. */
let epoch = 0;
class Stopped extends Error {}
const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('mapmark-sync') : null;

function schedule(delayMs: number, replace = false): void {
  const at = Date.now() + delayMs;
  if (!replace && at >= timerAt) return; // something sooner is already set
  clearTimeout(timer);
  timerAt = at;
  timer = window.setTimeout(() => {
    timerAt = Infinity;
    void syncNow();
  }, delayMs);
}

/** A map changed here (maps.ts): remember it, and sync when the hand rests. */
export function changed(id: string): void {
  dirtyIds.add(id);
  blobIds.add(id);
  requestSync('change');
}

export function requestSync(reason: SyncReason = 'change'): void {
  if (!app.state.session) return;
  if (reason === 'now') return schedule(0);
  if (reason === 'foreground') {
    thorough = true;
    if (Date.now() - lastOkAt >= FOREGROUND_MIN_MS || app.state.sync !== 'synced') schedule(0);
    return;
  }
  if (app.state.sync === 'synced') app.set({ sync: 'pending' });
  // put away already: now, while the page still runs
  if (document.visibilityState === 'hidden') return schedule(0);
  // in trouble (offline, the server refusing): the retry already set will take this change along
  if (setbacks > 0 && timerAt !== Infinity) return;
  // when the hand rests — but never put off for longer than MAX_WAIT_MS
  const t = Date.now();
  if (!firstChangeAt) firstChangeAt = t;
  schedule(Math.max(0, Math.min(IDLE_MS, firstChangeAt + MAX_WAIT_MS - t)), true);
}

export function startSyncTriggers(): void {
  window.addEventListener('online', () => {
    app.set({ online: true });
    setbacks = 0;
    thorough = true;
    requestSync('now');
  });
  window.addEventListener('offline', () => app.set({ online: false }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') requestSync('foreground');
    else if (app.state.sync === 'pending') requestSync('now');
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) requestSync('foreground');
  });
  // another tab synced: show what it brought, and trust the store over our hints
  channel?.addEventListener('message', () => {
    thorough = true;
    void publishMaps();
  });
}

/** One sync, now (or the one already running). Never rejects: what happened is in `app.state.sync`. */
export function syncNow(): Promise<void> {
  if (!app.state.session) return Promise.resolve();
  if (running) {
    again = true;
    return running;
  }
  clearTimeout(timer);
  timerAt = Infinity;
  firstChangeAt = 0;
  running = (async () => {
    let waiting = true;
    try {
      app.set({ sync: 'syncing', syncError: null });
      const moved = await exclusively(cycle);
      if (moved === null) {
        // another tab is at it; what is dirty here may have missed its start
        again = true;
        app.set({ sync: 'pending' });
        return;
      }
      lastOkAt = Date.now();
      waiting = dirtyIds.size > 0 || blobIds.size > 0;
      // work that is waiting although nothing new came in did not move: do not spin on it
      setbacks = waiting && !again && !moved ? setbacks + 1 : 0;
      app.set({ sync: waiting ? 'pending' : 'synced', online: true });
    } catch (e) {
      thorough = true;
      if (e instanceof Stopped) {
        waiting = false;
        return;
      }
      setbacks += 1;
      waiting = dirtyIds.size > 0 || blobIds.size > 0;
      if (e instanceof OfflineError) app.set({ sync: 'offline', online: false });
      else if (e instanceof ApiFailure && e.status === 401) app.set({ sync: 'idle' });
      else {
        console.error('sync failed', e);
        app.set({ sync: 'error', syncError: describeError(e) });
      }
    } finally {
      running = null;
      const more = again;
      again = false;
      if (app.state.session && app.state.sync !== 'idle') {
        if (setbacks > 0) {
          if (waiting) schedule(backoff(setbacks));
        } else if (more || waiting) schedule(document.visibilityState === 'hidden' ? 0 : IDLE_MS); // put away: now, while the page still runs
      }
    }
  })();
  return running;
}

function backoff(n: number): number {
  const base = Math.min(300_000, 2_000 * 2 ** Math.min(n - 1, 8));
  return Math.round(base * (0.75 + Math.random() * 0.5));
}

/** One tab at a time. Resolves null when another tab holds the lock. */
async function exclusively<T>(fn: () => Promise<T>): Promise<T | null> {
  if (!navigator.locks?.request) return fn();
  return navigator.locks.request('mapmark-sync', { ifAvailable: true }, (lock) => (lock ? fn() : null)) as Promise<T | null>;
}

/** The cycle's session: the user it runs for, while that user is still the one signed in and the data its own. */
let session: { user: string; epoch: number } | null = null;
function still(): void {
  if (!session || session.epoch !== epoch || app.state.session?.user.id !== session.user) throw new Stopped();
}
const cursorKey = () => `syncSeq:${session!.user}`;
const checkedKey = () => `syncCheckedAt:${session!.user}`;

/** Write one map (in one transaction) and, in the same breath, show it: screen and store never part. */
async function commit(id: string, fn: (cur: LocalMap | undefined) => LocalMap | null | undefined, blobs?: Record<string, Blob>): Promise<LocalMap | null | undefined> {
  still();
  const out = await mutateMap(id, fn, blobs);
  if (out !== undefined) show(id, out);
  return out;
}
/** Put one map's new state on screen (no re-read of the store; nothing is set when nothing visible changed). */
export function show(id: string, m: LocalMap | null): void {
  const prev = app.state.maps;
  const i = prev.findIndex((x) => x.id === id);
  const live = m && m.deletedAt === null ? m : null;
  if (!live) {
    if (i >= 0) app.set({ maps: prev.filter((x) => x.id !== id) });
    return;
  }
  if (i >= 0 && mapSignature(prev[i]!) === mapSignature(live)) {
    prev[i] = live; // same to the eye: keep the list (and the screen) as it is, the record current
    return;
  }
  const next = i >= 0 ? prev.map((x) => (x.id === id ? live : x)) : [...prev, live].sort((a, b) => b.createdAt - a.createdAt);
  app.set({ maps: next });
}

/* ------------------------------------------------------------------ one cycle */

const needsBlobWork = (m: LocalMap): boolean => m.deletedAt === null && (!m.imageUploaded || !m.thumbUploaded || ((m.thumbRev ?? 0) > 0 && m.thumbHave !== m.thumbRev));

/** Resolves true when anything went up or came down. */
async function cycle(): Promise<boolean> {
  const user = app.state.session?.user.id;
  if (!user) throw new Stopped();
  session = { user, epoch };
  let cursor = (await getKV<number>(cursorKey())) ?? 0;
  const check = Date.now() - ((await getKV<number>(checkedKey())) ?? 0) > CHECK_EVERY_MS;
  const full = thorough || check;
  thorough = false;
  if (full) {
    // the store is the truth: make the hints anew from it
    dirtyIds.clear();
    blobIds.clear();
    for (let m of await listMaps()) {
      // (once a day a map the server refused is offered again: what refused it may have been put right)
      if (check && m.blocked) m = (await mutateMap(m.id, (cur) => (cur ? { ...cur, blocked: undefined } : undefined))) ?? m;
      if (m.dirty && !m.blocked) dirtyIds.add(m.id);
      if (needsBlobWork(m)) blobIds.add(m.id);
    }
  }
  let index: [string, number, number][] | undefined;
  let moved = false;
  const tried = new Set<string>();

  for (let round = 0; round < 200; round++) {
    // what is dirty now — read afresh each round: the editor may have saved again meanwhile
    const parts: string[] = [];
    const sent = new Map<string, number>();
    let bytes = 0;
    let left = 0;
    for (const id of [...dirtyIds]) {
      const m = await getMap(id);
      if (!m || !m.dirty || m.blocked) {
        dirtyIds.delete(id);
        continue;
      }
      if (tried.has(`${id}@${m.updatedAt}`)) continue; // (answered already, and not for the better: not twice in one cycle)
      const j = JSON.stringify(toPut(m));
      if (parts.length && bytes + j.length > BATCH_BYTES) {
        left += 1;
        continue;
      }
      parts.push(j);
      sent.set(id, m.updatedAt);
      tried.add(`${id}@${m.updatedAt}`);
      bytes += j.length;
    }
    if (round > 0 && !parts.length && !left) break;
    const body = `{"since":${cursor},"puts":[${parts.join(',')}]${round === 0 && check ? ',"check":true' : ''}}`;
    // a small upload as the app is put away is allowed to outlive the page
    const keepalive = document.visibilityState === 'hidden' && body.length < 60_000;
    const res = await apiJson<SyncResult>('/api/sync', { method: 'POST', body }, { keepalive });
    noteServerTime(res.serverTime);

    for (const ack of res.acks) {
      const sentAt = sent.get(ack.id);
      if (sentAt === undefined) continue;
      if (ack.status === 'rejected') console.error('the server refused map', ack.id, ack.error);
      const out = await commit(ack.id, (cur) => applyAck(cur, ack, sentAt));
      if (out === null || (out && (!out.dirty || out.blocked))) dirtyIds.delete(ack.id);
      if (out && needsBlobWork(out)) blobIds.add(ack.id);
      if (ack.status !== 'rejected') moved = true;
    }
    for (const rec of res.maps) {
      const out = await commit(rec.id, (cur) => applyRecord(cur, rec));
      if (out === undefined) continue;
      moved = true;
      if (out === null) {
        dirtyIds.delete(rec.id);
        blobIds.delete(rec.id);
      } else if (needsBlobWork(out)) blobIds.add(rec.id);
    }
    if (res.index) index = res.index;
    cursor = res.cursor;
    still();
    await setKV(cursorKey(), cursor);
    if (!res.more && !left) break;
  }

  if (index) {
    // the daily check, against everything as it now stands
    const { fetch, resend } = checkIndex(await listMaps(), index);
    for (const id of fetch) {
      try {
        const rec = await apiJson<MapRecord>(`/api/maps/${id}`);
        const out = await commit(id, (cur) => applyRecord(cur, rec));
        if (out) {
          moved = true;
          if (needsBlobWork(out)) blobIds.add(id);
        }
      } catch (e) {
        if (!(e instanceof ApiFailure && e.status === 404)) throw e;
      }
    }
    for (const id of resend) {
      const out = await commit(id, (cur) => (cur && !cur.dirty ? { ...cur, dirty: true, rev: 0, imageUploaded: false, thumbUploaded: false, updatedAt: Math.max(now(), cur.updatedAt + 1) } : undefined));
      if (out) {
        dirtyIds.add(id);
        again = true;
      }
    }
  }
  still();
  if (check) await setKV(checkedKey(), Date.now());

  const before = cursor;
  let blobsMoved: boolean;
  [cursor, blobsMoved] = await blobs(cursor, check);
  still();
  if (cursor !== before) await setKV(cursorKey(), cursor);
  if (moved || blobsMoved) channel?.postMessage('maps');
  return moved || blobsMoved;
}

/** Plan images and thumbnails: up where the server lacks ours, down where ours is out of date. */
async function blobs(cursor: number, checking: boolean): Promise<[number, boolean]> {
  // Our own upload moves the server's counter by one: when that is all that moved, the cursor
  // moves with it, and the next sync is not told about our own picture.
  const advance = (s: BlobStored) => {
    if (s.seq === cursor + 1) cursor = s.seq;
  };
  const gone = (e: unknown) => e instanceof ApiFailure && (e.status === 404 || e.status === 410);
  let moved = false;
  for (const id of [...blobIds]) {
    const m = await getMap(id);
    if (!m || m.deletedAt !== null) {
      blobIds.delete(id);
      continue;
    }
    if (m.rev === 0) {
      // the server must know the map first: it waits for its document to go up — unless that was refused
      if (m.blocked || !m.dirty) blobIds.delete(id);
      continue;
    }
    let waiting = false;
    if (!m.imageUploaded) {
      const blob = await getBlob(`${id}:orig`);
      if (blob) {
        try {
          const s = (await (await apiFetch(`/api/maps/${id}/image`, { method: 'PUT', body: blob, headers: { 'content-type': m.imageType } }, { timeoutMs: 180_000 })).json()) as BlobStored;
          advance(s);
          await commit(id, (cur) => (cur ? { ...cur, imageUploaded: s.hasImage } : undefined));
          moved = true;
        } catch (e) {
          if (!gone(e)) throw e; // deleted elsewhere: the tombstone is on its way
        }
      }
    }
    if (!m.thumbUploaded) {
      const blob = await getBlob(`${id}:thumb`);
      const stamp = m.thumbStamp;
      if (blob) {
        try {
          const s = (await (await apiFetch(`/api/maps/${id}/thumb`, { method: 'PUT', body: blob, headers: { 'content-type': 'image/jpeg' } })).json()) as BlobStored;
          advance(s);
          // (a thumbnail made again while this one went up still has to go)
          const out = await commit(id, (cur) => (cur ? { ...cur, thumbUploaded: cur.thumbStamp === stamp ? s.hasThumb : false, thumbRev: s.thumbRev, thumbHave: s.thumbRev } : undefined));
          waiting = !!out && !out.thumbUploaded;
          moved = true;
        } catch (e) {
          if (!gone(e)) throw e;
        }
      }
    } else if ((m.thumbRev ?? 0) > 0 && (m.thumbHave !== m.thumbRev || (checking && !(await hasBlob(`${id}:thumb`))))) {
      // another device made a newer thumbnail (or, on the daily check, ours has gone missing)
      try {
        const res = await apiFetch(`/api/maps/${id}/thumb`);
        const rev = Number(res.headers.get('x-thumb-rev') ?? m.thumbRev);
        const blob = await res.blob();
        await commit(id, (cur) => (cur && cur.thumbUploaded ? { ...cur, thumbHave: rev, thumbStamp: now() } : undefined), { [`${id}:thumb`]: blob });
        moved = true;
      } catch (e) {
        if (!gone(e)) throw e;
        await commit(id, (cur) => (cur ? { ...cur, thumbHave: cur.thumbRev } : undefined));
      }
    }
    // done with this one — unless what it needs is simply not on this device (nothing to wait for either)
    if (!waiting) blobIds.delete(id);
  }
  return [cursor, moved];
}

/** The maps, read from the store, to the screen — only when something a person can see has changed. */
export async function publishMaps(): Promise<void> {
  const maps = stableList(app.state.maps, (await listMaps()).filter((m) => m.deletedAt === null));
  if (maps !== app.state.maps) app.set({ maps });
}

/** Ensure the full-resolution image is available locally (downloads if needed). */
export async function ensureImage(id: string): Promise<Blob | null> {
  const local = await getBlob(`${id}:orig`);
  if (local) return local;
  const res = await apiFetch(`/api/maps/${id}/image`, {}, { timeoutMs: 180_000 }).catch((e) => {
    if (e instanceof ApiFailure && e.status === 404) return null; // its device has not sent it yet
    throw e;
  });
  if (!res) return null;
  const blob = await res.blob();
  await mutateMap(id, (cur) => cur, { [`${id}:orig`]: blob });
  return blob;
}

/** Forget everything local (sign out). Any sync under way stops writing at once. */
export async function clearLocalData(): Promise<void> {
  epoch += 1;
  clearTimeout(timer);
  timerAt = Infinity;
  dirtyIds.clear();
  blobIds.clear();
  thorough = true;
  setbacks = 0;
  const user = app.state.session?.user.id ?? (await getKV<string>('owner'));
  for (const m of await listMaps()) await mutateMap(m.id, () => null);
  if (user) {
    await setKV(`syncSeq:${user}`, 0);
    await setKV(`syncCheckedAt:${user}`, 0);
  }
  await setKV('owner', null);
  app.set({ maps: [], sync: 'idle' });
}

/**
 * A device holds one inspector's maps at a time. Signing in as someone else (after a forced
 * sign-out, say, which leaves the maps in place so nothing unsent is lost to their owner) first
 * clears what the other left: it is never shown to, nor uploaded by, anyone but its owner.
 */
export async function adoptDevice(userId: string): Promise<void> {
  const owner = await getKV<string>('owner');
  if (owner && owner !== userId) await clearLocalData();
  if (owner !== userId) await setKV('owner', userId);
}
